"use client"

import type { ComponentProps } from "react"
import { Badge } from "~/components/legacy/common/badge"
import { Card, CardDescription, CardHeader } from "~/components/legacy/common/card"
import { H4 } from "~/components/legacy/common/heading"
import { Link } from "~/components/legacy/common/link"
import { ShowMore } from "~/components/legacy/common/show-more"
import { Skeleton } from "~/components/legacy/common/skeleton"
import { Stack } from "~/components/legacy/common/stack"
import { ToolRepositoryStats } from "~/components/web/tools/tool-repository-stats"
import { Favicon } from "~/components/legacy/web/ui/favicon"
import type { ToolMany } from "~/server/web/tools/payloads"

type ToolCardProps = ComponentProps<typeof Card> & {
  tool: ToolMany
  reasonLabels?: readonly string[]
}

const ToolCard = ({ tool, reasonLabels = [], ...props }: ToolCardProps) => {
  const primaryRepository = tool.repositories[0]

  return (
    <Card isRevealed {...props}>
      <CardHeader wrap={false}>
        <Favicon src={tool.faviconUrl} title={tool.name} contained />

        <H4 as="h3" className="truncate">
          <Link href={`/tools/${tool.slug}`}>
            <span className="absolute inset-0 z-10" />
            {tool.name}
          </Link>
        </H4>
      </CardHeader>

      <div className="relative size-full flex flex-col">
        <Stack size="lg" direction="column" className="flex-1 duration-200 group-hover:opacity-0">
          {tool.tagline && <CardDescription className="min-h-10">{tool.tagline}</CardDescription>}

          {primaryRepository ? (
            <ToolRepositoryStats repository={primaryRepository} />
          ) : (
            <ShowMore
              items={tool.categories}
              limit={1}
              getKey={category => category.name}
              renderItem={({ name }) => <Badge variant="outline">{name}</Badge>}
              size="xs"
              showMoreType="text"
              className="mt-auto"
            />
          )}

          {reasonLabels.length > 0 && (
            <Stack size="xs" className="-mt-1">
              {reasonLabels.map(label => (
                <Badge key={label} variant="info" className="max-w-full whitespace-normal">
                  {label}
                </Badge>
              ))}
            </Stack>
          )}
        </Stack>

        {tool.description && (
          <div className="absolute inset-0 opacity-0 duration-200 group-hover:opacity-100">
            <CardDescription className="line-clamp-3">{tool.description}</CardDescription>
          </div>
        )}
      </div>
    </Card>
  )
}

const ToolCardSkeleton = () => {
  return (
    <Card hover={false} className="items-stretch select-none">
      <CardHeader>
        <Favicon src="/favicon.svg" className="animate-pulse opacity-25 grayscale" contained />

        <H4 className="w-2/3">
          <Skeleton>&nbsp;</Skeleton>
        </H4>
      </CardHeader>

      <CardDescription className="flex flex-col gap-0.5">
        <Skeleton className="h-5 w-4/5">&nbsp;</Skeleton>
        <Skeleton className="h-5 w-1/2">&nbsp;</Skeleton>
      </CardDescription>

      <Stack size="sm" className="mt-auto">
        {Array.from({ length: 2 }, (_, i) => `badge-skeleton-${i}`).map(id => (
          <Badge key={id} variant="outline" className="w-12">
            &nbsp;
          </Badge>
        ))}
      </Stack>
    </Card>
  )
}

export { ToolCard, ToolCardSkeleton }
